import { istTimeOrDateTime, readCoverage, type CoverageInput } from '@/lib/analyticsReport';
import { USAGE_ANALYTICS_COPY } from '../copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  HOW MUCH OF THE WINDOW THE RECORD HOLDS. [ANALYTICS §5]
 *
 *  ⚠️ ONCE, FOR THE WHOLE PAGE, AND OUTSIDE EVERY SECTION. `coverage` sits at the root of the
 *  report rather than in `overview`, because a gap shortens every section by the same minutes —
 *  and rendered inside the Overview it would vanish exactly when that section failed, which is
 *  when a reader most needs to know the record is incomplete.
 *
 *  ⚠️ A GAP IS SAID AS A GAP. "The rest are not quiet minutes" is the sentence the wire's own
 *  comment asks for: a shortfall read as zeros is a quiet night that never happened.
 *
 *  ⚠️ AND A WINDOW THAT COULD NOT BE READ IS NOT A GAP. `readCoverage` answers `unread` for it, and
 *  the page says the record could not be read rather than "0% of minutes were captured" about a
 *  record that may be whole.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const CoverageNotice = ({ report }: { report: CoverageInput }) => {
  const copy = USAGE_ANALYTICS_COPY.overview;
  const coverage = readCoverage(report);
  const lines =
    coverage.kind === 'unread'
      ? [copy.coverageUnread]
      : [
          coverage.gapPercent === null ? null : copy.coverageGap(coverage.gapPercent),
          report.coverage.dataThrough === null
            ? null
            : copy.dataThrough(istTimeOrDateTime(report.coverage.dataThrough, report.generatedAt)),
          report.coverage.dropped > 0 ? copy.dropped(report.coverage.dropped) : null,
        ].filter((line): line is string => line !== null);

  if (lines.length === 0) return null;
  return (
    <div className="flex flex-col gap-1 text-sm text-muted-foreground" data-testid="analytics-coverage">
      {lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
};
